import React, { useContext, useEffect, useRef } from 'react';
import { useCallbackOne } from 'use-memo-one';
import type { IframeProps } from '../custom-ui/iframe/types';
import { ThreeLOPrompt } from '../components/threeLOPrompt';
import { useSharedSandbox, destroySandboxConnection } from './useSharedSandbox';
import { destroySandboxIframe, getOrCreateSandboxIframe } from './SharedSandbox';
import { EnvironmentContext } from '../context';
import { createWorkerBridge } from '@atlassian/bridge-core/v2';
import { getForgeSandboxOrigin, makeEncodedAppContext } from './utils';
import { createBridgeFeatures } from '../bridge/createBridgeFeatures';
import { useBridgeContext } from '../bridge/useBridgeContext';
import { useBridgeServices } from '../bridge/useBridgeServices';
import { useRuntimeOverlayController } from './useRuntimeOverlayController';
import { TracingProvider } from '../error-reporting/tracing';

type WorkerWebRuntimeProps = IframeProps & {
	appId: string;
};

/**
 * Renders a Forge app using the worker-based runtime.
 *
 * Instead of an `<iframe>` per app (as the classic {@link Iframe} renderer does),
 * this component runs all app code inside dedicated module Workers spawned from a
 * single shared hidden sandbox `<iframe>`. This improves isolation and reduces
 * browser resource usage when multiple Forge apps are on the same page.
 *
 * **Lifecycle**
 * 1. On mount, waits for the shared sandbox connection to be established
 *    (see {@link useSharedSandbox}).
 * 2. Calls `connection.controller.createApp()` to spin up a Worker for this app
 *    and receives a `workerId`.
 * 3. Opens a {@link createWorkerBridge} channel to the Worker for bridge
 *    communication (UI events, resolver calls, etc.).
 * 4. On unmount, closes the bridge and calls `terminateApp(workerId)` to tear
 *    down the Worker.
 * 5. On page unload (`beforeunload`), destroys the shared sandbox connection and
 *    removes the singleton iframe from the DOM.
 *
 * This component renders `null` — it has no visible DOM output of its own.
 * The sandbox `<iframe>` is managed imperatively outside React so the reconciler
 * can never unmount or reorder it.
 */
const WorkerWebRuntimeBase = (props: WorkerWebRuntimeProps): React.JSX.Element | null => {
	const { appId, extension } = props;

	const environment = useContext(EnvironmentContext);

	// Shared, lazily-created sandbox iframe — a single <iframe> in document.body
	// is reused across all WorkerWebRuntime instances on the same page.
	const sandbox = useSharedSandbox(environment);

	// Tracks the active worker id so the unmount cleanup can terminate it.
	const workerIdRef = useRef<string | null>(null);

	const { getBridgeFeatures, destroy: cleanupBridgeFeatures, overlay } = useBridgeFeatures(props);

	// Worker lifecycle: create on mount (once sandbox is ready), terminate on unmount.
	useEffect(() => {
		if (!sandbox.ready) {
			return;
		}

		const { connection, iframeEl } = sandbox;

		const encodedAppContext = makeEncodedAppContext(extension);

		// [TODO] we will need to review if it is possible that appContext can be changed during the lifecyle of the component.
		// If that is case, we might need to add appContext in the dependency array of useEffect and handle the update of appContext accordingly.
		const appContext = {
			appId,
			extension: {
				installationId: extension.installationId,
				environmentId: extension.environmentId,
				appVersion: extension.appVersion,
				properties: {
					resourceUploadId: extension.properties.resourceUploadId,
					resource: extension.properties.resource,
				},
				type: extension.type,
			},
			environment,
			encodedAppContext,
		};

		let bridgeClose: (() => void) | null = null;
		let cancelled = false;

		connection.controller
			.createApp(appContext)
			.then((workerId) => {
				// If the component unmounted while createApp() was in-flight, immediately
				// tear down the spawned worker so it isn't leaked.
				if (cancelled) {
					connection.controller.terminateApp(workerId).catch(() => {});
					return;
				}

				workerIdRef.current = workerId;

				const bridge = createWorkerBridge({
					workerId,
					withWindow: iframeEl.contentWindow!,
					withDomain: getForgeSandboxOrigin(environment),
					features: getBridgeFeatures(),
					sendHandshakeMetric: (...args) => {
						// TODO: implement proper metrics here. For now we just log to console to verify that the bridge is working.
						// eslint-disable-next-line no-console
						console.log('[worker] Handshake metric', ...args);
					},
				});

				return bridge.open();
			})
			.then((result) => {
				if (result) {
					bridgeClose = result.close;
				}
			})
			.catch((err) => {
				// eslint-disable-next-line no-console
				console.error('Failed to create worker app', err);
			});

		return () => {
			cancelled = true;
			cleanupBridgeFeatures();
			bridgeClose?.();
			if (workerIdRef.current !== null) {
				connection.controller.terminateApp(workerIdRef.current).catch((err) => {
					// eslint-disable-next-line no-console
					console.error(`Failed to terminate worker with id ${workerIdRef.current}`, err);
				});
				workerIdRef.current = null;
			}
		};
	}, [sandbox.ready]); // eslint-disable-line react-hooks/exhaustive-deps

	// Page-level cleanup — fires when the tab/window is closed or navigated away.
	// Destroys the shared connection and removes the singleton iframe entirely.
	useEffect(() => {
		const handleUnload = () => {
			const iframe = getOrCreateSandboxIframe(environment);
			if (iframe !== null) {
				destroySandboxConnection(iframe);
				destroySandboxIframe();
			}
		};

		window.addEventListener('beforeunload', handleUnload);
		return () => window.removeEventListener('beforeunload', handleUnload);
	}, []); // eslint-disable-line react-hooks/exhaustive-deps
	// Note that we intentionally do NOT include `environment` in the dependency array here.
	// The unload handler should be added only once and always refer to the same shared iframe,

	// The iframe is appended imperatively to document.body by the singleton —
	// overlay is null by default and populated only when a bridge method
	// calls overlayController.show().
	return <>{overlay}</>;
};

export const WorkerWebRuntime: React.MemoExoticComponent<
	(props: WorkerWebRuntimeProps) => React.JSX.Element
> = React.memo((props: WorkerWebRuntimeProps): React.JSX.Element => {
	return (
		<TracingProvider>
			<WorkerWebRuntimeBase {...props} />
		</TracingProvider>
	);
});

export const isAppUsingWorkerRuntime = (extension: IframeProps['extension']): boolean => {
	return extension.properties?.renderRuntimeType === 'webworker';
};

const useBridgeFeatures = (props: WorkerWebRuntimeProps) => {
	const environment = useContext(EnvironmentContext);
	// Single overlay slot — bridge methods call overlayController.show()/hide() to
	// render overlay UI into this slot. Animated mode (default) wraps in
	// <ModalTransition> for ADS modals; inline mode (animated=false) renders
	// ThreeLOPrompt directly without transition.
	const { overlay, overlayController } = useRuntimeOverlayController();

	// WorkerWebRuntime has no iframe to hide and no setIsThreeLOPromptVisible prop
	// (IframeProps vs InnerIframeProps), so renderThreeLOPrompt is a minimal
	// implementation — retry queue only, no visibility side-effects.
	const invokeRetriesRef = useRef<Array<() => Promise<void>>>([]);
	const latestRef = useRef({ overlayController, extension: props.extension });
	latestRef.current = { overlayController, extension: props.extension };

	const renderThreeLOPrompt = useCallbackOne(
		(authUrl: string, retry: () => Promise<void>): Promise<void> =>
			new Promise<void>((resolve) => {
				const { overlayController, extension } = latestRef.current;
				invokeRetriesRef.current.push(retry);
				overlayController.show(
					<ThreeLOPrompt
						authUrl={authUrl}
						onSuccess={async () => {
							resolve();
							overlayController.hide();
							try {
								await Promise.all(invokeRetriesRef.current.map((r) => r()));
							} finally {
								invokeRetriesRef.current = [];
							}
						}}
						metadata={{ moduleType: extension.type }}
					/>,
					true,
				);
			}),
		[],
	);

	// Assembles host-side capabilities (invoker, tracing, token, modal) for bridge methods.
	// Returns a getter rather than a snapshot so services are always current at call time.
	const { getServices } = useBridgeServices(props, overlayController, renderThreeLOPrompt);

	// [Important] the implementation of getBridgeContext is stable and does not change across the lifecycle of the component, so it is safe to include it in the dependency array of createBridgeFeatures
	// without causing unnecessary re-renders or re-creations of the bridge features.
	const { getBridgeContext, destroy } = useBridgeContext({ ...props, environment }, getServices);

	const getBridgeFeatures = useCallbackOne(() => createBridgeFeatures(getBridgeContext), []);

	return {
		overlay,
		destroy,
		getBridgeFeatures,
	};
};
